import { useState } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Megaphone, 
  Image, 
  Store, 
  LayoutTemplate,
  BarChart3,
  Settings,
  Users,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import clsx from 'clsx'

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  children?: { name: string; href: string }[]
}

const navigation: NavItem[] = [
  { name: 'ダッシュボード', href: '/', icon: LayoutDashboard },
  { name: 'キャンペーン', href: '/campaigns', icon: Megaphone },
  { name: 'クリエイティブ', href: '/creatives', icon: Image },
  { 
    name: '店舗管理', 
    href: '/stores', 
    icon: Store,
    children: [
      { name: '店舗一覧', href: '/stores' },
      { name: '店舗グループ', href: '/store-groups' },
    ]
  },
  { name: '広告枠', href: '/ad-slots', icon: LayoutTemplate },
  { name: 'レポート', href: '/reports', icon: BarChart3 },
]

const adminNavigation: NavItem[] = [
  { name: '設定', href: '/settings', icon: Settings },
  { name: 'ユーザー管理', href: '/users', icon: Users },
]

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const toggleMenu = (name: string) => {
    setExpandedMenus(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : [...prev, name]
    )
  }

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  const NavLink = ({ item }: { item: NavItem }) => {
    const active = isActive(item.href)
    const hasChildren = item.children && item.children.length > 0
    const expanded = expandedMenus.includes(item.name)

    if (hasChildren) {
      return (
        <div>
          <button
            onClick={() => toggleMenu(item.name)}
            className={clsx(
              'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              active
                ? 'bg-primary-600 text-white'
                : 'text-gray-300 hover:bg-slate-700 hover:text-white'
            )}
          >
            <div className="flex items-center">
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </div>
            <ChevronDown className={clsx('w-4 h-4 transition-transform', expanded && 'rotate-180')} />
          </button>
          {expanded && (
            <div className="ml-8 mt-1 space-y-1">
              {item.children?.map((child) => (
                <Link
                  key={child.href}
                  to={child.href}
                  className={clsx(
                    'block px-3 py-2 rounded-lg text-sm transition-colors',
                    location.pathname === child.href
                      ? 'bg-slate-700 text-white'
                      : 'text-gray-400 hover:bg-slate-700 hover:text-white'
                  )}
                >
                  {child.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      )
    }

    return (
      <Link
        to={item.href}
        className={clsx(
          'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors',
          active
            ? 'bg-primary-600 text-white'
            : 'text-gray-300 hover:bg-slate-700 hover:text-white'
        )}
      >
        <item.icon className="w-5 h-5 mr-3" />
        {item.name}
      </Link>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-slate-800 transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 bg-slate-900">
            <Link to="/" className="flex items-center">
              <span className="text-xl font-bold text-white">🎮 DTL Pro</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink key={item.name} item={item} />
            ))}

            <div className="pt-4 mt-4 border-t border-slate-700">
              {adminNavigation.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
            </div>
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'ユーザー'}</p>
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-2 p-2 text-gray-400 hover:text-white rounded-lg hover:bg-slate-700"
                title="ログアウト"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <button className="relative p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                <Bell className="w-6 h-6" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </button>

              {/* User menu (desktop) */}
              <div className="hidden lg:flex items-center">
                <span className="text-sm text-gray-700">{user?.name}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

